import React, { useState, useEffect } from 'react';
import ColaboradorLayout from '../../components/ColaboradorLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { assignmentsAPI } from '../../services/api';
import { FileText, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const HistoryPage = () => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const response = await assignmentsAPI.getAll({});
      // Sort by date descending
      const sorted = response.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setAssignments(sorted);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed': return 'badge-success';
      case 'cancelled': return 'badge-error';
      case 'no_show': return 'badge-warning';
      default: return 'badge-info';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'assigned': return 'Asignado';
      case 'confirmed': return 'Confirmado';
      case 'cancelled': return 'Cancelado';
      case 'no_show': return 'No asistió';
      default: return status;
    }
  };

  return (
    <ColaboradorLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Historial de Asignaciones
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Registro completo de tus asistencias presenciales
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
          </div>
        ) : assignments.length === 0 ? (
          <Card className="border border-slate-200">
            <CardContent className="flex flex-col items-center justify-center h-64 text-center">
              <FileText className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500">No tienes historial de asignaciones</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">
                Total: {assignments.length} asignaciones
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Día</th>
                      <th>Estado</th>
                      <th>Motivo Asignación</th>
                      <th>Equidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignments.map((a) => (
                      <tr key={a.id}>
                        <td className="font-medium">
                          {format(new Date(a.date), 'dd/MM/yyyy')}
                        </td>
                        <td>
                          {format(new Date(a.date), 'EEEE', { locale: es })}
                        </td>
                        <td>
                          <span className={getStatusBadge(a.status)}>
                            {getStatusLabel(a.status)}
                          </span>
                        </td>
                        <td className="text-sm text-slate-600 max-w-xs truncate">
                          {a.assignment_reason || '-'}
                        </td>
                        <td>
                          {a.equity_score ? (
                            <span className="badge-info">{a.equity_score}%</span>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Summary */}
        {assignments.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card className="border border-slate-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-[#002FA7]">{assignments.length}</p>
                <p className="text-xs text-slate-500">Total</p>
              </CardContent>
            </Card>
            <Card className="border border-slate-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">
                  {assignments.filter(a => a.status === 'confirmed').length}
                </p>
                <p className="text-xs text-slate-500">Confirmadas</p>
              </CardContent>
            </Card>
            <Card className="border border-slate-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-yellow-600">
                  {assignments.filter(a => a.status === 'assigned').length}
                </p>
                <p className="text-xs text-slate-500">Pendientes</p>
              </CardContent>
            </Card>
            <Card className="border border-slate-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-slate-600">
                  {Math.round(assignments.reduce((sum, a) => sum + (a.equity_score || 0), 0) / assignments.length) || 0}%
                </p>
                <p className="text-xs text-slate-500">Equidad Promedio</p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </ColaboradorLayout>
  );
};

export default HistoryPage;
